import EditCourseMaterial from "@/components/EditCourseMaterial/EditCourseMaterial";
import MaterialRow from "@/components/MaterialRow/MaterialRow";
import { ViewChapter } from "@/components/ViewChapter/ViewChapter";
import { useAppSelector } from "@/config/store";
import { Card, Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoCourseMaterial } from "@/shared/model/edo-course-material.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { FolderOpen, Plus } from "lucide-react";
import axios from "axios";
import { useEffect, useState } from "react";
import { Translate } from "react-jhipster";
import DeleteCourseMaterial from "../courses/components/DeleteCourseMaterial";

const ArchivioMateriale = () => {
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const { updateSuccess, deleteSuccess } = useAppSelector<
    EntityState<IEdoCourseMaterial>
  >((state) => state.edoCourseMaterial);
  const [tabValue, setTabValue] = useState<number | null>(null);
  const [viewChapterDialog, setViewChapterDialog] = useState<boolean>(false);
  const [chapterToView, setChapterToView] = useState<IEdoChapter | null>(null);
  const [courses, setCourses] = useState<IEdoCourse[]>([]);
  const [courseMaterials, setCourseMaterials] = useState<IEdoCourseMaterial[]>(
    []
  );
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [courseMaterialToEdit, setCourseMaterialToEdit] =
    useState<IEdoCourseMaterial | null>(null);
  const [editCourseMaterialDialog, setEditCourseMaterialDialog] =
    useState<boolean>(false);
  const [deleteMaterialDialog, setDeleteMaterialDialog] =
    useState<boolean>(false);
  const [materialToDelete, setMaterialToDelete] =
    useState<IEdoCourseMaterial | null>(null);

  const handleChangeTab = (newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenViewChapter = (chapter: IEdoChapter) => {
    setChapterToView(chapter);
    setViewChapterDialog(true);
  };

  const handleCloseViewChapter = () => {
    setViewChapterDialog(false);
    setChapterToView(null);
  };

  const handleOpenEditCourseMaterial = (
    courseMaterial: IEdoCourseMaterial | null
  ) => {
    setCourseMaterialToEdit(courseMaterial);
    setEditCourseMaterialDialog(true);
  };

  const handleCloseEditCourseMaterialDialog = () => {
    setEditCourseMaterialDialog(false);
    setCourseMaterialToEdit(null);
  };

  const handleOpenDeleteCourseMaterialDialog = (
    courseMaterial: IEdoCourseMaterial
  ) => {
    setMaterialToDelete(courseMaterial);
    setDeleteMaterialDialog(true);
  };

  const handleCloseDeleteCourseMaterialDialog = () => {
    setDeleteMaterialDialog(false);
    setMaterialToDelete(null);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      setLoadingCourses(true);
      const response = await axios.get<{ data: IEdoCourse[] }>(
        `/teachers/${account.id}/courses`
      );
      setCourses(response.data.data);
      setTabValue(response.data?.data?.[0]?.id ?? null);
      setLoadingCourses(false);
    };
    fetchCourses();
  }, [account.id]);

  useEffect(() => {
    const fetchMaterials = async (courseId: number) => {
      const response = await axios.get(`/courses/${courseId}/course-materials`, {
        params: {
          sortBy: "order:ASC",
          "filter.author.id": `$eq:${account.id}`,
        },
      });
      setCourseMaterials(response.data.data);
    };
    if (tabValue) {
      fetchMaterials(tabValue);
    }
  }, [tabValue, updateSuccess, deleteSuccess, account.id]);

  const getCourseMaterials = () => {
    if (!courseMaterials.length) {
      return (
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-[var(--color-mockup-text-light)]">
            <Translate contentKey="edocendoApp.edoCourseMaterial.home.notFound" />
          </p>
        </div>
      );
    }
    return (
      <div className="divide-y divide-[var(--color-mockup-border)]">
        {courseMaterials.map((courseMaterial, index, list) => (
          <MaterialRow
            key={courseMaterial.id}
            courseMaterial={courseMaterial}
            isLastElement={list.length - 1 === index}
            onOpenViewChapter={handleOpenViewChapter}
            onOpenEditMaterial={handleOpenEditCourseMaterial}
            onOpenDeleteMaterial={handleOpenDeleteCourseMaterialDialog}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        {courses.length > 0 && (
          <Tabs
            value={tabValue === null ? "" : String(tabValue)}
            onValueChange={(v) => handleChangeTab(Number(v))}
            className="w-full"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] p-4 bg-[var(--color-surface-secondary)]/30">
              <TabsList className="flex h-auto w-full sm:w-auto flex-wrap gap-2 overflow-x-auto border-0 bg-transparent p-0 justify-start">
                {courses.map((course) => (
                  <TabsTrigger
                    key={course.id}
                    value={String(course.id)}
                    className="whitespace-nowrap rounded-full border border-[var(--color-mockup-border)] bg-surface px-4 py-2 text-xs font-bold data-[state=active]:bg-[var(--color-mockup-teal)] data-[state=active]:text-white data-[state=active]:border-transparent shadow-sm"
                  >
                    {course.subject?.name} - {course.class?.year?.year}°
                    {course.class?.section}
                  </TabsTrigger>
                ))}
              </TabsList>
              <button
                onClick={() => handleOpenEditCourseMaterial(null)}
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
              >
                <Plus className="h-4 w-4" />
                Aggiungi materiale
              </button>
            </div>
            {courses.map((course) => (
              <TabsContent
                key={course.id}
                value={String(course.id)}
                className="mt-0 p-0 focus-visible:ring-0 focus-visible:outline-none"
              >
                {tabValue === course.id ? getCourseMaterials() : null}
              </TabsContent>
            ))}
          </Tabs>
        )}
        {loadingCourses && (
          <div className="flex justify-center py-8">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}
        {courses.length === 0 && !loadingCourses && (
          <div className="px-6 py-12 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <FolderOpen className="h-5 w-5 text-primary" />
            </div>
            <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
              Nessun corso trovato per questo account.
            </p>
          </div>
        )}
      </Card>
      <EditCourseMaterial
        courseMaterial={courseMaterialToEdit ?? undefined}
        open={editCourseMaterialDialog}
        onClose={handleCloseEditCourseMaterialDialog}
      />
      <DeleteCourseMaterial
        open={deleteMaterialDialog}
        courseMaterial={materialToDelete ?? undefined}
        onClose={handleCloseDeleteCourseMaterialDialog}
      />
      <ViewChapter
        open={viewChapterDialog}
        chapter={chapterToView}
        onClose={handleCloseViewChapter}
      />
    </>
  );
};

export default ArchivioMateriale;
